import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import {
  parseSessionProjectionEnvelopeV1,
  type SessionProjectionEnvelopeV1,
} from '../core/session/session-projection.js'
import { logger } from './logger-client.js'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Validate and accept a server-owned session projection at a client boundary.
 * Invalid, stale, and wrong-session envelopes never mutate the browser view.
 */
function readEnvelopeForOpenSession(
  value: unknown,
  source: string,
): SessionProjectionEnvelopeV1 | null {
  try {
    const envelope = parseSessionProjectionEnvelopeV1(value)
    const expectedSessionId = clientSessionViewStore.getSnapshot().sessionId

    if (expectedSessionId !== envelope.sessionId) {
      logger.warn('[SessionProjection] Rejected projection for another session', {
        source,
        expectedSessionId,
        receivedSessionId: envelope.sessionId,
        receivedVersion: envelope.version,
      })
      return null
    }

    return envelope
  } catch (error) {
    logger.warn('[SessionProjection] Rejected malformed projection envelope', {
      source,
      error: error instanceof Error ? error.message : String(error),
    })
    return null
  }
}

export function acceptSessionProjectionEnvelope(
  value: unknown,
  source: string
): boolean {
  const envelope = readEnvelopeForOpenSession(value, source)
  if (!envelope) return false
  return clientSessionViewStore.acceptServerProjection(envelope)
}

/** Facts and version only. The live transcript stays on the rows already on screen. */
export function acceptLiveSessionFactsEnvelope(value: unknown, source: string): boolean {
  const envelope = readEnvelopeForOpenSession(value, source)
  if (!envelope) return false
  return clientSessionViewStore.acceptLiveSessionFacts(envelope)
}

/** Accept a nested `projectionEnvelope` when an endpoint response includes one. */
export function acceptEmbeddedSessionProjection(
  response: unknown,
  source: string
): boolean {
  if (!isRecord(response) || response.projectionEnvelope === undefined) {
    return false
  }

  return acceptSessionProjectionEnvelope(response.projectionEnvelope, source)
}
